import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: string;
  siteName?: string;
  locale?: string;
}

export const SEO: React.FC<SEOProps> = ({
  title,
  description,
  keywords,
  image,
  url = window.location.href,
  type = 'website',
  siteName = 'SPID Consultants',
  locale = 'fr_CA',
}) => {
  const { t } = useTranslation();
  
  const defaultTitle = t('companyName');
  const defaultDescription = t('companyDescription');
  const defaultImage = `${window.location.origin}/images/og-image.jpg`;
  
  const seo = {
    title: title ? `${title} | ${defaultTitle}` : defaultTitle,
    description: description || defaultDescription,
    image: image || defaultImage,
    url,
    type,
    siteName,
    locale,
    keywords: keywords || 'SPID, Consultants, IT, conseil, solutions digitales, architecture, implémentation, Webdesk, bureau virtuel, cloud',
  };

  return (
    <Helmet>
      <title>{seo.title}</title>
      <meta name="description" content={seo.description} />
      <meta name="keywords" content={seo.keywords} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={seo.type} />
      <meta property="og:url" content={seo.url} />
      <meta property="og:title" content={seo.title} />
      <meta property="og:description" content={seo.description} />
      <meta property="og:image" content={seo.image} />
      <meta property="og:site_name" content={seo.siteName} />
      <meta property="og:locale" content={seo.locale} />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={seo.url} />
      <meta name="twitter:title" content={seo.title} />
      <meta name="twitter:description" content={seo.description} />
      <meta name="twitter:image" content={seo.image} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={seo.url} />
    </Helmet>
  );
};

export const WebdeskSEO: React.FC = () => {
  const { t } = useTranslation();
  
  return (
    <SEO
      title={t('solutionsSection.webdesk.title')}
      description={t('solutionsSection.webdesk.description')}
      keywords="Webdesk, bureau virtuel, cloud, bureau en ligne, applications web, SaaS, virtual desktop, cloud computing"
      image={`${window.location.origin}/images/webdesk.png`}
      type="product"
    />
  );
};

export default SEO;
